import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  presets?: {
    label: string;
    range: DateRange;
  }[];
}

const makeRange = (daysAgo: number): DateRange => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - daysAgo);
  startDate.setHours(0, 0, 0, 0);
  return { startDate, endDate };
};

const makeYtdRange = (): DateRange => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date(new Date().getFullYear(), 0, 1);
  startDate.setHours(0, 0, 0, 0);
  return { startDate, endDate };
};

const getDefaultPresets = () => [
  { label: 'Last 7 days', range: makeRange(7) },
  { label: 'Last 30 days', range: makeRange(30) },
  { label: 'Last 3 months', range: makeRange(90) },
  { label: 'Last 6 months', range: makeRange(180) },
  { label: 'This year', range: makeYtdRange() }
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  presets = getDefaultPresets()
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const formatDate = (date: Date) => formatDateUtil(date);

  const handlePresetSelect = (preset: { label: string; range: DateRange }) => {
    onChange(preset.range);
    setIsOpen(false);
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', dateString: string) => {
    // Parse as local date (YYYY-MM-DD) to avoid UTC midnight shift
    const [year, month, day] = dateString.split('-').map(Number);
    const newDate = new Date(year, month - 1, day);
    if (!isNaN(newDate.getTime())) {
      if (field === 'startDate') {
        newDate.setHours(0, 0, 0, 0);
      } else {
        newDate.setHours(23, 59, 59, 999);
      }
      onChange({
        ...value,
        [field]: newDate
      });
    }
  };

  // Format date as YYYY-MM-DD using local time (not UTC)
  const toLocalDateString = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {formatDate(value.startDate)} - {formatDate(value.endDate)}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Date Range</h3>
            
            {/* Presets */}
            <div className="space-y-2 mb-4">
              <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wider">Quick Select</h4>
              <div className="grid grid-cols-2 gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handlePresetSelect(preset)}
                    className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date Inputs */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wider">Custom Range</h4>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={toLocalDateString(value.startDate)}
                    onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={toLocalDateString(value.endDate)}
                    onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={() => setIsOpen(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
